import React from 'react';
import {
  INBOX_CATCH_UP_STALE_MS,
  shouldScheduleInboxCatchUpSync,
} from '../utils/inboxSyncSchedule';

type InboxAccountLike = {
  id: string | number;
  sync_since?: string | number | null;
  nylas_grant_id?: string | null;
};

type UseInboxAutoSyncOptions = {
  account: InboxAccountLike | null;
  onSync: (accountId: string | number, options?: { silent?: boolean }) => void | Promise<void>;
};

export function useInboxAutoSync({ account, onSync }: UseInboxAutoSyncOptions) {
  const inFlightRef = React.useRef(false);
  const lastCatchUpAtByAccountRef = React.useRef(new Map<string, number>());
  const onSyncRef = React.useRef(onSync);
  onSyncRef.current = onSync;

  const accountId = account?.id ?? null;
  const hasSyncSince = account?.sync_since != null && account.sync_since !== '';
  const hasNylasGrant = account?.nylas_grant_id != null && String(account.nylas_grant_id).trim() !== '';

  React.useEffect(() => {
    inFlightRef.current = false;
  }, [accountId]);

  const triggerCatchUp = React.useCallback(
    (reason: 'mount' | 'focus' | 'interval') => {
      if (accountId == null) return;
      const lastCatchUpAtMs = lastCatchUpAtByAccountRef.current.get(String(accountId)) ?? null;
      if (
        !shouldScheduleInboxCatchUpSync({
          visible: document.visibilityState === 'visible',
          hasSyncSince,
          hasNylasGrant,
          inFlight: inFlightRef.current,
          reason,
          lastCatchUpAtMs,
        })
      ) {
        return;
      }
      inFlightRef.current = true;
      void Promise.resolve(onSyncRef.current(accountId, { silent: true }))
        .catch(() => {
          // silent catch-up — manual refresh remains available
        })
        .finally(() => {
          inFlightRef.current = false;
          lastCatchUpAtByAccountRef.current.set(String(accountId), Date.now());
        });
    },
    [accountId, hasNylasGrant, hasSyncSince],
  );

  React.useEffect(() => {
    if (accountId == null) return;
    triggerCatchUp('mount');
  }, [accountId, hasNylasGrant, hasSyncSince, triggerCatchUp]);

  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') triggerCatchUp('focus');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [triggerCatchUp]);

  React.useEffect(() => {
    if (accountId == null) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      triggerCatchUp('interval');
    };

    const timer = window.setInterval(tick, INBOX_CATCH_UP_STALE_MS);
    return () => window.clearInterval(timer);
  }, [accountId, triggerCatchUp]);
}
